import dayjs from 'dayjs';
import _ from 'lodash';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getIncidentHash, getNotificationDetails } from '@/lib';
import { getAddressFromCoord, mapbox_accessToken } from '@/lib/utils';
import useLocalStorage from '@/lib/useLocalStorage.js';
import { sdkManager, setupSdkManager } from '@/sdkManagerSetup';

const ERSContext = createContext({});
export const useERSContext = () => useContext(ERSContext);

export function ERSContextProvider({ children, onLoadingChange }) {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cameraPopups, setCameraPopups] = useState([]);

  // Report connection state to parent for unified loading UI
  useEffect(() => {
    onLoadingChange?.(!isConnected);
  }, [isConnected, onLoadingChange]);

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const groupsRef = useRef([]);
  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);
  const usersRef = useRef([]);
  useEffect(() => {
    usersRef.current = users;
  }, [users]);
  const userProfilesRef = useRef({});

  const [POIs, setPOIs] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [cameras, setCameras] = useState([]);

  const [groupMembers, setGroupMembers] = useState({});
  const [chatHistory, setChatHistory] = useState({});
  const chatHistoryRef = useRef({});
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [incidentAssigneeStatusList, setIncidentAssigneeStatusList] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [unreadInfo, setUnreadInfo] = useState({});
  const [editingIncidents, setEditingIncidents] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [userSpeaking, setUserSpeaking] = useState({});
  const [incomingVideoStreams, setIncomingVideoStreams] = useState([]);

  const [liveEvents, setLiveEvents] = useState([]);

  const [pinnedIds, setPinnedIds] = useLocalStorage('pinnedIds', []);
  const [latestResponder, setLatestResponder] = useState([]);

  const [selectedChatId, setSelectedChatId] = useState('');
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMembers, setChatMembers] = useState({});
  const [pinnedChats, setPinnedChats] = useState(new Set());
  const [mutedChats, setMutedChats] = useState(new Set());

  //NOTE: chatHistories is removed, the correct variable is chatHistory and it is defined already. The old code that uses chatHistories are all modified to use correct state variable chatHistory

  const [scrollToChatFunction, setScrollToChatFunction] = useState(() => {});

  const [editingGeofenceFromPopMap, setEditingGeofenceFromPopMap] = useState({});
  const [editingMarkerFromPopMap, setEditingMarkerFromPopMap] = useState({});

  const popMapWindowRef = useRef(null);
  const popMapChannelRef = useRef(null);
  const checkedPOIsRef = useRef([]);
  const checkedGeofencesRef = useRef([]);
  const checkedCamerasRef = useRef([]);

  function postMessageToPopMapWindow({ type, payload }) {
    if (popMapChannelRef.current) popMapChannelRef.current.postMessage({ type, payload });
    /*
    let delay = 0;
    //if (!popMapWindowRef.current || popMapWindowRef.current.closed) delay = 1000; //delay to wait for the new window open
    setTimeout(() => {
      popMapChannelRef.current.postMessage({ type, payload });
    }, delay);
    */
  }

  // Function to register the scroll function from ChatSidebar
  const registerScrollToChat = useCallback(scrollFn => {
    setScrollToChatFunction(() => scrollFn);
  }, []);

  // Add this effect to handle selectedChatId changes
  const handleSelectedChatIdChange = useCallback(
    newSelectedChatId => {
      setSelectedChatId(newSelectedChatId);

      if (!newSelectedChatId || !chatList?.length) {
        return;
      }

      let matchingChat = chatList.find(chat => {
        // Try exact match first
        if (chat?.id === newSelectedChatId) return true;

        // Try matching without domain
        const chatIdWithoutDomain = chat?.id?.split('@')[0];
        if (chatIdWithoutDomain === newSelectedChatId) return true;

        // Try matching with domain removed from selectedChatId if it has one
        const selectedIdWithoutDomain = newSelectedChatId.split('@')[0];
        if (chatIdWithoutDomain === selectedIdWithoutDomain) return true;

        return false;
      });

      if (matchingChat) {
        setSelectedChat(matchingChat);
      } else {
        setSelectedChat(null);
        console.warn('No matching chat found for selectedChatId:', newSelectedChatId);
      }
    },
    [chatList, setSelectedChat, setSelectedChatId]
  );

  //state for SOS modal
  const [currentSOS, setCurrentSOS] = useState(null);

  const selfProfileRef = useRef(null);

  const notifiedIncidentsRef = useRef(new Map()); // Stores { incidentId: lastNotifiedHash }

  const AVAILABLECOLORS = [
    'bg-red-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-lime-500',
  ];

  function getGroupOrUser(id, givenContacts) {
    if (!id) return null;
    const allEntities = givenContacts || [...groups, ...users];

    // Try exact match first
    const exactMatch = allEntities.find(entity => entity.id === id);
    if (exactMatch) return exactMatch;

    // Try prefix match (before "@")
    const idPrefix = id.split('@')[0];
    return allEntities.find(entity => entity.id.split('@')[0] === idPrefix) || null;
  }

  function getContactByName(name, givenContacts) {
    const allEntities = givenContacts || [...groups, ...users];
    return allEntities.find(c => c.displayName === name);
  }

  function mergeUserWithProfile(user, profileMap) {
    const idPrefix = user.id?.split('@')[0];
    const profile = profileMap[idPrefix] || profileMap[user.id];
    if (!profile) return user;
    return {
      ...user,
      username: profile.username,
      phone_number: profile.phone_number,
      department_name: profile.department_name,
      first_name: profile.first_name,
      last_name: profile.last_name,
    };
  }

  async function initialFetchChatHistory(item) {
    try {
      const chatBox = await wbSdk.chatManager.getChatBox(item.id, item.type === 'group');
      if (chatBox) {
        const chatList = await chatBox.fetchAndGetMessages(null, 20, 0);
        setChatHistory(prevChatHistory => ({
          ...prevChatHistory,
          [item.id]: { messages: chatList, chatBox },
        }));
        return chatList;
      }
    } catch (e) {
      console.error('Error fetching chat history:', e);
    }
    return null;
  }

  async function getUnreadFor(theContact) {
    try {
      const chatBox = await sdkManager.chatManager.getChatBox(theContact.id, theContact.type === 'group');
      const count = await chatBox.getUnreadCount();
      if (count > 0) {
        const now = new Date();
        const timeStr = now.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
        const dateStr = now.toLocaleDateString('en-GB', {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit',
        });
        const formatted = `${timeStr} ${dateStr}`;
        setUnreadInfo(prev => ({
          ...prev,
          [theContact.id]: {
            count,
            time: formatted,
            timestamp: now.getTime(),
          },
        }));
      } else {
        setUnreadInfo(prev => ({
          ...prev,
          [theContact.id]: {
            count: 0,
          },
        }));
      }
    } catch (e) {
      console.error(`Failed to load unread info for ${theContact.id}`, e);
    }
  }

  async function getUnreadForAllWhenStart(contacts) {
    const arr = [...contacts.groups, ...contacts.users];
    const batchSize = 5;
    for (let i = 0; i < arr.length; i += batchSize) {
      const batch = arr.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async contact => {
          try {
            if (unreadInfo[contact.id]) return;
            getUnreadFor(contact);
          } catch (e) {
            console.error(`Failed to load unread info for ${id}`, e);
          }
        })
      );
    }
  }

  function initContactsCallback(contacts, extendedRoster) {
    if (extendedRoster !== true) return; //to ensure only one execution
    //console.log(contacts)
    if (!isConnected) setIsLoading(false);

    //fetch all unread message count of each pinned contact ID
    getUnreadForAllWhenStart(contacts);

    setIsConnected(true);
    setUsers(contacts.users);
    setGroups(contacts.groups);

    //check any user that has SOS stats and send live event
    contacts.users.forEach(user => {
      if (user.statuses?.includes('emergency')) {
        setLiveEvents(prev => {
          const exists = prev.some(ev => ev.contactId === user.id && ev.type === 'SOS');
          if (exists) return prev;

          return [
            {
              contactId: user.id,
              direction: 'incoming',
              isGroup: false,
              type: 'SOS',
              timestamp: Date.now(),
            },
            ...prev,
          ];
        });
      }
    });
  }

  function showCameraStreamPopup(itemWithStreamURI) {
    if (!itemWithStreamURI.uri) return;
    setCameraPopups(prev => {
      const exists = prev.find(p => p.id === itemWithStreamURI.id);
      if (exists) return prev; // prevent duplicate
      return [
        ...prev,
        {
          id: itemWithStreamURI.id,
          displayName: itemWithStreamURI.displayName,
          url: itemWithStreamURI.uri,
          lng: itemWithStreamURI.location[1],
          lat: itemWithStreamURI.location[0],
          loopTimeLength: itemWithStreamURI.loopTimeLength,
          type: itemWithStreamURI.type,
        },
      ];
    });
  }

  //when new contact add to pinnedid, check their unread message number
  useEffect(() => {
    if (!isConnected) return;

    pinnedIds.forEach(async id => {
      if (unreadInfo[id]) return; // already queried
      const theContact = getGroupOrUser(id);
      if (!theContact) return;
      getUnreadFor(theContact);
    });
  }, [pinnedIds, isConnected]);

  // set the latest status of the responders
  useEffect(() => {
    try {
      if (!incidents.length || !users.length) return;

      // Step 1: Filter MobileUnit users
      const mobileUnitUsers = _.filter(users, { permission: 'MobileUnit' });

      // Step 2: Build a flattened array of { assigneeId, incident, assignee }
      const assigneeIncidentPairs = incidents?.flatMap(incident =>
        (incident.assignees || []).map(assignee => ({
          assigneeId: assignee.id,
          assignee,
          incident,
        }))
      );

      // Step 3: Group incidents by assigneeId for quick lookup
      const incidentsByAssignee = _.groupBy(assigneeIncidentPairs, 'assigneeId');

      // Step 4: Map over each user and find their latest incident status
      const latestResponderStatus = _.map(mobileUnitUsers, user => {
        const shortUserId = user.id.split('@')[0];
        const userIncidents = incidentsByAssignee[shortUserId] || [];

        // Skip if user has no assigned incidents
        if (!userIncidents.length) {
          return {
            id: user.id || '',
            displayName: user.displayName || '-',
            groupName: user.groupName || '—',
            presence: user.presence || 'Unknown',
            permission: user.permission,
            incidentName: '—',
            statuses: [],
            latestIncident: null,
            assignee: null,
          };
        }

        // Step 5: Compute timestamps for each incident and find latest
        const incidentsWithTimestamps = _.map(userIncidents, ({ incident, assignee }) => {
          const timestamps = [
            ...(_.map(assignee?.status_records, r => (r.timestamp ? dayjs.unix(r.timestamp).valueOf() : 0)) || []),
            incident.startTimestamp ? dayjs.unix(incident.startTimestamp).valueOf() : 0,
            incident.lastUpdateTimestamp ? dayjs.unix(incident.lastUpdateTimestamp).valueOf() : 0,
          ];
          return {
            incident,
            assignee,
            latestTime: _.max(timestamps) || 0,
          };
        });

        // Step 6: Find the incident with the most recent update
        const latestIncidentPair = _.maxBy(incidentsWithTimestamps, 'latestTime');

        const latestIncident = latestIncidentPair?.incident;
        const assignee = latestIncidentPair?.assignee;
        const assigneeStatuses = _.map(assignee?.status_records, 'status') || [];

        // Step 7: Return formatted responder data
        return {
          id: user.id || '',
          displayName: user.displayName || '-',
          groupName: user.groupName || '—',
          presence: user.presence || 'Unknown',
          permission: user.permission,
          incidentName: latestIncident?.title || '—',
          statuses: assigneeStatuses,
          latestIncident,
          assignee,
          users: user,
          allAssignments: _.map(userIncidents, ({ incident, assignee }) => ({
            incidentId: incident.id,
            incidentName: incident.title,
            incidentStatus: incident.status || 'unknown',
            statusHistory: assignee?.status_records || [],
            response_status: assignee?.response_status || 'pending',
            assigneeRecord: assignee,
            incidentStartTimestamp: incident.startTimestamp,
            incidentLastUpdateTimestamp: incident.lastUpdateTimestamp,
          })).filter(a => {
            const startOfDay = dayjs().startOf('day').unix();
            const timestamp = a.incidentLastUpdateTimestamp || a.incidentStartTimestamp;
            return timestamp >= startOfDay;
          }),
        };
      });
      setLatestResponder?.(latestResponderStatus);
    } catch (err) {
      setLatestResponder?.([]);
      console.error('Error in responder status:', err);
    }
  }, [incidents, users]);

  function handlePTVIncomingSession(contactIdBeforeParsing, speaking) {
    console.debug('ptvIncomingSession', contactIdBeforeParsing, speaking);

    setIncomingVideoStreams(prev => {
      const existingIndex = prev.findIndex(entry => entry.contactId === contactIdBeforeParsing);

      if (existingIndex !== -1) {
        // Item exists: update the 'completed' attribute based on 'speaking'
        const updated = [...prev];
        const targetItem = { ...updated[existingIndex] };

        if (speaking) {
          delete targetItem.completed;
        } else {
          targetItem.completed = true;
        }

        updated[existingIndex] = targetItem;
        return updated;
      }
      return [{ contactId: contactIdBeforeParsing, stream: null }, ...prev];
    });
  }

  function handlePlayIncomingVideo(stream) {
    console.debug('playIncomingVideo', stream);
    setIncomingVideoStreams(prev => {
      const updated = [...prev];
      const emptyIndex = updated.findIndex(v => !v.stream);
      if (emptyIndex !== -1) {
        updated[emptyIndex] = { ...updated[emptyIndex], stream };
      } else {
        // fallback: push unknown if somehow no session was pre-created
        updated.push({ contactId: 'unknown', stream });
      }
      return updated;
    });
  }

  function handleActivityRecordAdded(record) {
    console.debug('ActivityRecordAdded', record);
    // Handle SOS activity records
    if (record.type === 'SOS' && record.direction === 'incoming') {
      // Find the user details
      const user = getGroupOrUser(record.contactId);
      if (user) {
        const loc = user.lastKnownLocation;
        const hasCoords = loc?.lat != null && loc?.long != null;
        const fallbackLocation = hasCoords ? `${loc.lat}, ${loc.long}` : 'Location not available';

        setCurrentSOS({
          incidentId: user.id,
          userId: record.contactId,
          userName: user.displayName || 'Unknown User',
          location: loc?.address || fallbackLocation,
          incident: 'SOS Alert',
          timestamp: record.time.getTime(),
          geoloc: loc,
          status: user.status,
        });

        // Reverse geocode to resolve address if only coords are available
        if (!loc?.address && hasCoords) {
          getAddressFromCoord(loc.lat, loc.long, mapbox_accessToken).then(address => {
            if (address) {
              setCurrentSOS(prev => (prev?.userId === record.contactId ? { ...prev, location: address } : prev));
            }
          });
        }
      }
    }
    if (record.type === 'PTV' && record.status === 'completed' && record.contactId) {
      setIncomingVideoStreams(prev =>
        prev.map(item => (item.contactId === record.contactId ? { ...item, completed: true } : item))
      );
    }
  }

  function handleIncidentsUpdate(updatedIncidents) {
    updatedIncidents.forEach(incident => {
      if (incident.type === 'SOS' && incident.status === 'progress') {
        const geo = incident.geoloc;
        const hasCoords = geo?.lat != null && geo?.long != null;
        const fallbackLocation = hasCoords ? `${geo.lat}, ${geo.long}` : 'Location not available';

        setCurrentSOS({
          incidentId: incident.id,
          userId: incident.subject_id,
          userName: incident.reporter_info || 'Unknown User',
          location: incident.location?.display_name || fallbackLocation,
          incident: incident.title,
          timestamp: incident.creationTimestamp,
          geoloc: geo,
          status: incident.status,
        });

        // Reverse geocode to resolve address if only coords are available
        if (!incident.location?.display_name && hasCoords) {
          getAddressFromCoord(geo.lat, geo.long, mapbox_accessToken).then(address => {
            if (address) {
              setCurrentSOS(prev => (prev?.incidentId === incident.id ? { ...prev, location: address } : prev));
            }
          });
        }
      }
    });

    // ==================== INCIDENT UPDATE LOGIC ====================
    setIncidents(prev => {
      const aIds = new Set(prev.map(item => item.id));
      const updatedMap = new Map(updatedIncidents.map(i => [i.id, i]));
      const existing = prev.filter(i => !updatedMap.has(i.id));
      const merged = [...updatedIncidents, ...existing];

      // ==================== NOTIFICATION LOGIC ====================
      const prevMap = new Map(prev.map(i => [i.id, i]));
      const newNotifications = [];
      updatedIncidents.forEach(inc => {
        const currentHash = getIncidentHash(inc);
        const lastNotifiedHash = notifiedIncidentsRef.current.get(inc.id);

        // Only create notification if this specific change hasn't been notified
        if (lastNotifiedHash !== currentHash) {
          const isNewIncident = !aIds.has(inc.id);
          const previousIncident = prevMap.get(inc.id);

          // Use existing logic: if incident doesn't exist in prev, it's new
          if (isNewIncident) {
            // NEW INCIDENT - notification for creation
            const notificationDetails = getNotificationDetails(inc, true, null);

            newNotifications.push({
              id: uuidv4(),
              type: 'newIncident',
              ...notificationDetails, // Spread all the keys (titleKey, titleParams, etc.)
              timestamp: dayjs().unix(),
              isRead: false,
              incidentId: inc.id,
            });
          } else {
            // UPDATED INCIDENT - notification for update
            const notificationDetails = getNotificationDetails(inc, false, previousIncident);
            newNotifications.push({
              id: uuidv4(),
              type: 'updatedIncident',
              ...notificationDetails, // Spread all the keys (titleKey, changes, etc.)
              timestamp: dayjs().unix(),
              isRead: false,
              incidentId: inc.id,
            });
          }
          // Mark this change as notified
          notifiedIncidentsRef.current.set(inc.id, currentHash);
        }
      });

      // Update notifications if there are any new ones
      if (newNotifications.length > 0) {
        setNotifications(prevNotifs => [...newNotifications, ...prevNotifs]);
      }
      return merged;
    });
  }

  function handleContactTagsChange(data) {
    //if (data && data.length > 0) console.log('tags change:', data);
    setUsers(prevUsers => {
      let updatedUsers = [...prevUsers];

      data.forEach(update => {
        const user = getGroupOrUser(update.id, updatedUsers);
        if (!user) return;
        const cloneUser = { ...user, statuses: [...(user.statuses || [])] };
        let emergencyAdded = false;
        let emergencyRemoved = false;

        // Add statuses
        update.added?.forEach(tag => {
          if (!cloneUser.statuses.includes(tag.status)) {
            cloneUser.statuses.push(tag.status);
            if (tag.status === 'emergency') emergencyAdded = true;
          }
        });

        // Remove statuses
        update.removed?.forEach(tag => {
          if (tag.status === 'emergency') emergencyRemoved = true;
          cloneUser.statuses = cloneUser.statuses.filter(s => s !== tag.status);
        });

        updatedUsers = updatedUsers.map(u => (u.id === cloneUser.id ? cloneUser : u));

        // handle liveEvents update
        if (emergencyAdded) {
          setLiveEvents(prev => {
            const exists = prev.some(ev => ev.contactId === cloneUser.id && ev.type === 'SOS');
            if (exists) return prev;

            return [
              {
                contactId: cloneUser.id,
                direction: 'incoming',
                isGroup: cloneUser.type === 'group',
                type: 'SOS',
                timestamp: Date.now(),
              },
              ...prev,
            ];
          });
        }

        if (emergencyRemoved) {
          setLiveEvents(prev => prev.filter(ev => ev.contactId !== cloneUser.id || ev.type !== 'SOS'));
        }
      });
      return updatedUsers;
    });
  }

  const handleChatSelect = useCallback(
    async chat => {
      setSelectedChat(chat);

      //in SelectedChat module it will fetch chat history, there is no need to fetch chat history here

      if (chat.type === 'group') {
        const response = await wbSdk.xmppManager.fetchAllGroupMembers(chat.id);
        setChatMembers(prev => ({ ...prev, [chat.id]: response }));
      }
    },
    [chatMembers, unreadInfo]
  );

  const togglePinChat = useCallback(
    (chatId, dropTargetId = null) => {
      setPinnedIds(prev => {
        let newPinned = [...prev];

        if (dropTargetId && newPinned.includes(chatId) && newPinned.includes(dropTargetId) && chatId !== dropTargetId) {
          const oldIndex = newPinned.indexOf(chatId);
          const newIndex = newPinned.indexOf(dropTargetId);

          if (oldIndex !== -1 && newIndex !== -1) {
            const item = newPinned[oldIndex];
            const arr1 = newPinned.slice(0, newIndex + 1).filter(i => i !== chatId);
            const arr2 = newPinned.slice(newIndex + 1).filter(i => i !== chatId);

            if (oldIndex <= newIndex) {
              arr2.unshift(item);
            } else {
              arr1.splice(arr1.length - 1, 0, item);
            }

            newPinned = [...arr1, ...arr2];
          }
        } else {
          if (newPinned.includes(chatId)) {
            newPinned = newPinned.filter(id => id !== chatId);
          } else {
            newPinned.push(chatId);
          }
        }

        localStorage.setItem('pinnedChats', JSON.stringify(newPinned));
        setPinnedChats(new Set(newPinned));
        return newPinned;
      });
    },
    [setPinnedIds]
  );

  const toggleMuteChat = useCallback(
    async chatId => {
      try {
        const chat = chatList.find(chat => chat.id === chatId);
        const isGroup = chat?.type === 'group';
        const isCurrentlyMuted = mutedChats.has(chatId);

        if (isCurrentlyMuted) {
          await wbSdk.sipManager.unmuteContact(chatId, isGroup);
          setMutedChats(prev => {
            const newMuted = new Set(prev);
            newMuted.delete(chatId);
            localStorage.setItem('mutedChats', JSON.stringify([...newMuted]));
            return newMuted;
          });
        } else {
          await wbSdk.sipManager.muteContact(chatId, isGroup);
          setMutedChats(prev => {
            const newMuted = new Set(prev);
            newMuted.add(chatId);
            localStorage.setItem('mutedChats', JSON.stringify([...newMuted]));
            return newMuted;
          });
        }
      } catch (error) {
        console.error('Error toggling mute:', error);
        setMutedChats(prev => {
          const newMuted = new Set(prev);
          if (newMuted.has(chatId)) {
            newMuted.delete(chatId);
          } else {
            newMuted.add(chatId);
          }
          localStorage.setItem('mutedChats', JSON.stringify([...newMuted]));
          return newMuted;
        });
      }
    },
    [chatList, mutedChats]
  );

  const isChatMuted = useCallback(
    chatId => {
      return mutedChats.has(chatId);
    },
    [mutedChats]
  );

  const isChatPinned = useCallback(
    chatId => {
      return pinnedChats.has(chatId);
    },
    [pinnedChats]
  );

  async function initQueryData() {
    const re = await sdkManager.incidentManager.getIncidentTypes();
    setIncidentTypes(re);
    const re2 = await sdkManager.incidentManager.getIncidentSettings();
    setIncidentAssigneeStatusList(re2.incident_statuses);
    const re3 = await sdkManager.incidentManager.getAllIncidents();
    setIncidents(re3);

    const re4 = await wbSdk.mapsService.getAllOrgCamera();
    setCameras(re4);
    const re5 = await wbSdk.mapsService.getAllOrgGeofence();
    setGeofences(re5);
    const re6 = await wbSdk.mapsService.getAllPOIs();
    setPOIs(re6);

    updateSelfProfile();
    fetchAllUserProfiles();
  }

  async function updateSelfProfile() {
    selfProfileRef.current = await wbSdk.profileManager.userProfile;
  }

  async function fetchAllUserProfiles() {
    try {
      const token = await wbSdk.loginService.getAccessToken();
      const profileUrl = wbSdk.profileManager.authConfig.profileUrl;
      const currentUsers = usersRef.current.filter(u => u.type === 'user' && !u.isBot);

      const results = await Promise.allSettled(
        currentUsers.map(async user => {
          const userId = user.id.split('@')[0];
          const res = await fetch(`${profileUrl}/users/${userId}`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-Api-Version': 'v8',
            },
          });
          if (!res.ok) return null;
          const profile = await res.json();
          return { userId: user.id, profile };
        })
      );

      const profileMap = {};
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          profileMap[result.value.userId.split('@')[0]] = result.value.profile;
        }
      });
      userProfilesRef.current = profileMap;
      setUsers(prevUsers => prevUsers.map(user => mergeUserWithProfile(user, profileMap)));
    } catch (err) {
      console.error('Error fetching user profiles:', err);
    }
  }

  useEffect(() => {
    const handleContactChange = contacts => {
      contacts.forEach(({ contact, subscription }) => {
        const contactId = contact.id;
        const contactType = contact.type;
        if (contactType == 'group') {
          setGroups(prev => {
            const index = prev.findIndex(entity => entity.id === contactId);
            let updatedGroups;
            if (subscription === 'remove') {
              if (index === -1) return prev;
              updatedGroups = [...prev.slice(0, index), ...prev.slice(index + 1)];
            } else {
              if (index === -1) {
                updatedGroups = [...prev, contact];
              } else {
                updatedGroups = [contact, ...prev.slice(0, index), ...prev.slice(index + 1)];
              }
            }
            return updatedGroups;
          });
        } else if (contactType == 'user') {
          setUsers(prev => {
            const index = prev.findIndex(entity => entity.id === contactId);
            let updatedUsers;
            if (subscription === 'remove') {
              if (index === -1) return prev;
              updatedUsers = [...prev.slice(0, index), ...prev.slice(index + 1)];
            } else {
              const mergedContact = mergeUserWithProfile(contact, userProfilesRef.current);
              if (index === -1) {
                updatedUsers = [...prev, mergedContact];
              } else {
                updatedUsers = [mergedContact, ...prev.slice(0, index), ...prev.slice(index + 1)];
              }
            }
            return updatedUsers;
          });
        }
      });
    };

    const handleContactPresenceChange = presenceState => {
      //console.debug("presencestate",presenceState)
      setUsers(prevUsers => {
        const updatedUsers = prevUsers.map(user => {
          const updatedPresence = presenceState.find(p => p.jid === user.id);
          if (updatedPresence) {
            return {
              ...user,
              presence: updatedPresence.result,
              lastKnownLocation: updatedPresence.lastKnownLocation,
            };
          }
          return user;
        });
        return updatedUsers;
      });
    };

    const handleUpdateInMessages = updatedMsgs => {
      updatedMsgs.forEach(([chatMeta, newMessage, unknownFlag, action]) => {
        const originChatBoxId = chatMeta.jid;
        const chatBoxId = originChatBoxId.replace('@conference.', '@');

        const existing = chatHistory[chatBoxId];
        if (!existing) {
          initialFetchChatHistory({ id: chatMeta.jid, type: chatMeta.isGroup ? 'group' : 'user' });
        } else {
          setChatHistory(prevChatHistory => {
            const existing = prevChatHistory[chatBoxId];

            const messages = existing?.messages || [];
            const index = messages.findIndex(msg => msg.id === newMessage.id);

            let updatedMessages;

            if (action === 'remove') {
              if (index === -1) return prevChatHistory;
              updatedMessages = [...messages.slice(0, index), ...messages.slice(index + 1)];
            } else {
              if (index === -1) {
                updatedMessages = [...messages, newMessage];
              } else {
                updatedMessages = [...messages.slice(0, index), newMessage, ...messages.slice(index + 1)];
              }
            }

            return {
              ...prevChatHistory,
              [chatBoxId]: {
                ...existing,
                messages: updatedMessages,
              },
            };
          });
        }

        //add geofence alert to the live events
        if (originChatBoxId.startsWith('bot@') && action !== 'remove') {
          const enteredMatch = newMessage.body.match(/^(.+?) entered geofence (.+)$/);
          const leftMatch = newMessage.body.match(/^(.+?) left geofence (.+)$/);

          if (enteredMatch || leftMatch) {
            const [, contactName, geofenceName] = enteredMatch || leftMatch;
            const alertOn = enteredMatch ? 'entered' : 'left';
            if (contactName !== 'You') {
              setLiveEvents(prev => {
                const contact = getContactByName(contactName, [...groupsRef.current, ...usersRef.current]);
                return [
                  {
                    contactName,
                    contactId: contact?.id,
                    geofenceName,
                    alertOn,
                    type: 'geofence',
                    timestamp: newMessage.sendingDate,
                  },
                  ...prev,
                ];
              });
            }
          }
        }
      });
    };

    function handleIncomingSession(contactIdBeforeParsing, speaking) {
      //console.log(contactIdBeforeParsing, speaking);
      setUserSpeaking(prev => ({
        ...prev,
        [contactIdBeforeParsing]: speaking,
      }));

      //console.log("handleIncomingSession", contactIdBeforeParsing, speaking);
    }

    function handleUnreadCountChange(chatBox, newCount) {
      //NOTE: this callback will only be valid when at least the contact chatbox has once been called with chatBox.getUnreadCount
      //console.log("unread update",chatBox,newCount);
      const now = new Date();
      const formattedTime = now.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
      const formattedDate = now.toLocaleDateString('en-GB', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      });

      //TODO: to check with Synch, group chat incoming and shows the group id with "conference.", such as "grpjvzpay_1648@conference.widebridgecloud.com"
      //temporarily removing that conference. away for now
      const chatBoxId = chatBox.jid.replace('@conference.', '@');

      const audio = new Audio('/static/sounds/incomingMessageTone.mp3');
      audio.play().catch(e => console.error('Audio play failed:', e));
      setUnreadInfo(prev => ({
        ...prev,
        [chatBoxId]: {
          count: newCount,
          time: `${formattedTime} ${formattedDate}`,
          timestamp: now.getTime(), // Store timestamp for sorting,
        },
      }));
    }

    function handleGeofenceChange(SDKGf) {
      if (SDKGf.subscription === 'remove') {
        setGeofences(prev => prev.filter(p => p.id !== SDKGf.geoFence.id));
      } else if (SDKGf.subscription === 'update') {
        setGeofences(prev => prev.map(a => (a.id === SDKGf.geoFence.id ? { ...a, ...SDKGf.geoFence } : a)));
      } else if (SDKGf.subscription === 'add') {
        setGeofences(prev => [SDKGf.geoFence, ...prev]);
      }
    }

    function handleIncomingInteract(chatBox, message) {
      if (message?.interactData?.type == 'splash') {
        const contactId = chatBox.jid;
        const description = message.interactData.data.description;
        const isCritical = message.interactData.critical;
        setLiveEvents(prev => {
          const contact = getGroupOrUser(contactId, [...groupsRef.current, ...usersRef.current]);
          return [
            {
              contactName: contact.displayName,
              description,
              isCritical,
              contactId,
              type: 'splashAlert',
              timestamp: message?.message?.sendingDate,
            },
            ...prev,
          ];
        });
      }
    }

    function handlePOIChange(SDKPoi) {
      if (SDKPoi.subscription === 'remove') {
        setPOIs(prev => prev.filter(p => p.id !== SDKPoi.poi.id));
      } else if (SDKPoi.subscription === 'update') {
        setPOIs(prev => prev.map(a => (a.id === SDKPoi.poi.id ? { ...a, ...SDKPoi.poi } : a)));
      } else if (SDKPoi.subscription === 'add') {
        setPOIs(prev => [SDKPoi.poi, ...prev]);
      }
    }

    //below is for blocking the error causing app crash
    const handleUnhandledRejection = event => {
      console.error('Global Promise Rejection:', event.reason);
      event.preventDefault(); // Prevent app crash
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    //end of error handling

    // SDK Setup
    setupSdkManager({
      initContactsCallback,
      handleContactChange,
      handleContactPresenceChange,
      handleUpdateInMessages,
      handleUnreadCountChange,
      handleContactTagsChange,
      handleIncidentsUpdate,
      handleIncomingSession,
      handleIncomingInteract,
      handleGeofenceChange,
      handlePOIChange,
      handlePlayIncomingVideo,
      handleActivityRecordAdded,
      handlePTVIncomingSession,
    })
      .then(() => {
        //query initial data
        initQueryData();
      })
      .catch(error => {
        console.error('Error during SDK setup:', error);
      });
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    postMessageToPopMapWindow({ type: 'users', payload: users });
  }, [users]);
  useEffect(() => {
    postMessageToPopMapWindow({ type: 'incidents', payload: incidents });
  }, [incidents]);
  useEffect(() => {
    postMessageToPopMapWindow({ type: 'latestResponder', payload: latestResponder });
  }, [latestResponder]);

  async function handleMessageFromPopMapWindow(msg) {
    const type = msg?.type;
    if (type === 'newMapWindowReady') {
      postMessageToPopMapWindow({ type: 'ack-newpopmap' });
      setUsers(prev => {
        postMessageToPopMapWindow({ type: 'users', payload: prev });
        return prev;
      });
      setIncidents(prev => {
        postMessageToPopMapWindow({ type: 'incidents', payload: prev });
        return prev;
      });
      setIncidentAssigneeStatusList(prev => {
        postMessageToPopMapWindow({ type: 'incidentAssigneeStatusList', payload: prev });
        return prev;
      });
      setLatestResponder(prev => {
        postMessageToPopMapWindow({ type: 'latestResponder', payload: prev });
        return prev;
      });
      // Re-send checked POI markers so previously-checked POIs appear on the popup
      checkedPOIsRef.current.forEach(poi => {
        postMessageToPopMapWindow({
          type: 'showMarker',
          payload: {
            category: 'poi',
            item: poi,
            hasAfterDragFunc: false,
            hasShowCameraStreamPopup: false,
          },
        });
      });
      // Re-send checked geofences so previously-checked geofences appear on the popup
      checkedGeofencesRef.current.forEach(g => {
        postMessageToPopMapWindow({
          type: 'showGeofence',
          payload: { item: g, coordinates: g.coordinates },
        });
      });
      // Re-send checked camera markers so previously-checked cameras appear on the popup
      checkedCamerasRef.current.forEach(camera => {
        postMessageToPopMapWindow({
          type: 'showMarker',
          payload: {
            category: 'camera',
            item: camera,
            hasAfterDragFunc: false,
            hasShowCameraStreamPopup: false,
          },
        });
      });
    } else if (type === 'editingGeofenceFromPopMap') {
      setEditingGeofenceFromPopMap(msg.payload);
    } else if (type === 'markerDragged') {
      setEditingMarkerFromPopMap(msg.payload);
    }
  }

  useEffect(() => {
    //clean up the broadcast channel to pop map window when page is closed
    if (!popMapChannelRef.current) {
      popMapChannelRef.current = new BroadcastChannel('popmap_channel');
    }
    popMapChannelRef.current.onmessage = event => {
      handleMessageFromPopMapWindow(event.data);
    };
    return () => {
      if (popMapChannelRef.current) popMapChannelRef.current.close();
      popMapChannelRef.current = null;
    };
  }, []);

  return (
    <ERSContext.Provider
      value={{
        users,
        setUsers,
        groups,
        setGroups,
        cameraPopups,
        setCameraPopups,
        pinnedIds,
        setPinnedIds,
        isConnected,
        setIsConnected,
        groupMembers,
        setGroupMembers,
        chatHistory,
        setChatHistory,
        POIs,
        setPOIs,
        cameras,
        geofences,
        setGeofences,
        incidentTypes,
        setIncidentTypes,
        incidentAssigneeStatusList,
        setIncidentAssigneeStatusList,
        incidents,
        setIncidents,
        userSpeaking,
        setUserSpeaking,
        getGroupOrUser,
        unreadInfo,
        setUnreadInfo,
        initialFetchChatHistory,
        editingIncidents,
        setEditingIncidents,
        updateSelfProfile,
        selfProfileRef,
        liveEvents,
        setLiveEvents,
        incomingVideoStreams,
        setIncomingVideoStreams,
        latestResponder,
        setLatestResponder,
        currentSOS,
        setCurrentSOS,
        notifications,
        setNotifications,
        setIsLoading,
        selectedChatId,
        setSelectedChatId: handleSelectedChatIdChange,
        chatList,
        setChatList,
        selectedChat,
        setSelectedChat,
        chatMembers,
        setChatMembers,
        pinnedChats,
        setPinnedChats,
        mutedChats,
        setMutedChats,
        handleChatSelect,
        togglePinChat,
        toggleMuteChat,
        isChatMuted,
        isChatPinned,
        scrollToSelectedChat: scrollToChatFunction,
        registerScrollToChat,
        showCameraStreamPopup,
        popMapWindowRef,
        postMessageToPopMapWindow,
        checkedPOIsRef,
        checkedGeofencesRef,
        checkedCamerasRef,
        editingGeofenceFromPopMap,
        editingMarkerFromPopMap,
      }}
    >
      {isConnected && children}

      {/* hidden, just make sure tailwind css color are compiled in the build */}
      <div className="hidden">
        bg-red-500 bg-green-500 bg-yellow-500 bg-purple-500 bg-orange-500 bg-pink-500 bg-teal-500 bg-indigo-500
        bg-lime-500 text-red-500 text-green-500 text-yellow-500 text-purple-500 text-orange-500 text-pink-500
        text-teal-500 text-indigo-500 text-lime-500
      </div>
    </ERSContext.Provider>
  );
}
